import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { ApiError } from '@app/shared/api/error.api';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { GuiParams } from '@app/shared/store/gui-params/gui-params-facade.service';
import { LoginStateEnum } from '@app/authentication/login/login-state.enum';
import { BackButtonSupportService } from '@app/shared/services/back-button-support.service';
import { UsersControllerService } from '@app/generated/services/users-controller.service';
import { TrackingParametersComponent } from '@app/unauthenticated/trackingParameters.component';
import { LoginService } from '@app/authentication/shared/services/login.service';
import { TwoFactorService } from '@app/authentication/shared/services/two-factor.service';
import { TrezorService } from '@app/authentication/shared/services/trezor.service';
import { TwoFactorType } from '@app/authentication/shared/api/two-factor.api';
import { UserLoginDto } from '@app/authentication/shared/api/login-data.api';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { TwoFaLoginComponent } from '@app/authentication/login/2fa/2fa-login.component';
import { Router } from '@angular/router';
import { AppGtmService } from '@app/shared/services/gtm.service';
import { SeonService, SeonValidationActionType } from '@app/shared/services/seon.service';

@Component({
  selector: 'app-login-base',
  templateUrl: './login-base.component.html',
  styleUrls: [],
  standalone: false,
})
export class LoginBaseComponent extends TrackingParametersComponent implements OnInit, OnDestroy {
  @ViewChild('twoFaLoginComponent') twoFaLoginComponent!: TwoFaLoginComponent;

  readonly templateLoginStateEnum = LoginStateEnum;

  loginEmail = '';
  loginState: LoginStateEnum = LoginStateEnum.login;
  waiting = false;
  originalRequest: HttpRequest<any> | null = null;
  redirect?: string;

  private unsubscribe$ = new Subject<void>();

  constructor(
    @Inject(PLATFORM_ID) platformId: Object,
    private loginService: LoginService,
    private twoFactor: TwoFactorService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private seonService: SeonService,
    private trezor: TrezorService,
    private guiParams: GuiParams,
    private backButtonSupportService: BackButtonSupportService,
    private gtmService: AppGtmService,
    private usersService: UsersControllerService,
    private router: Router,
  ) {
    super(platformId);
  }

  private static getLogInState(twoFactorType: string): LoginStateEnum | null {
    switch (twoFactorType) {
      case TwoFactorType.deviceConfirmation:
        return LoginStateEnum.confirmationEmail;
      case TwoFactorType.email:
        return LoginStateEnum.twoFactorEmail;
      case TwoFactorType.googleAuth:
        return LoginStateEnum.twoFactorGoogle;
      case TwoFactorType.trezor:
        return LoginStateEnum.twoFactorTrezor;
      default:
        return null;
    }
  }

  private static isCsrfException(error: any): boolean {
    if (error instanceof HttpErrorResponse) {
      const errorBody: ApiError = error.error;
      if (
        error.status === 401 &&
        (errorBody.errorType === 'InvalidCsrfTokenException' || errorBody.errorType === 'MissingCsrfTokenException')
      ) {
        return true;
      }
    }
    return false;
  }

  private static isTwoFactorAuthenticationException(error: any): boolean {
    if (error instanceof HttpErrorResponse) {
      const errorBody: ApiError = error.error;
      if (error.status === 401 && errorBody.errorType === 'TwoFactorAuthenticationException') {
        return true;
      }
    }
    return false;
  }

  private static isInvalid2FA(error: any): boolean {
    if (error instanceof HttpErrorResponse) {
      const errorBody: ApiError = error.error;
      if (
        (error.status === 400 && errorBody.errorType === 'InvalidTwoFactorAuthenticationException') ||
        (error.status === 401 && errorBody.errorType === 'InvalidLoginTwoFactorAuthentication')
      ) {
        return true;
      }
    }

    return false;
  }

  private static isWrongCredentialsError(error: any): boolean {
    if (error instanceof HttpErrorResponse) {
      const errorBody: ApiError = error.error;
      if (error.status === 401 && errorBody.errorType === 'WrongCredentialsException') {
        return true;
      }
    }

    return false;
  }

  private redirectAfterLoggedInTo(url: string) {
    this.guiParams.loggedIn$
      .pipe(
        filter((loggedIn) => loggedIn),
        take(1),
      )
      .subscribe(() => {
        this.router.navigateByUrl(url);
      });
  }

  ngOnInit() {
    const urlParams = new URLSearchParams(window.location.search);
    this.activateAccount(urlParams.get('activation'));

    const redirect = urlParams.get('redirect');
    if (redirect?.startsWith('/')) {
      this.redirect = redirect;
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  activateAccount(code: string | null) {
    if (!code) {
      return;
    }

    this.usersService
      .activateAccountUsingPost({ body: code })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.toastr.success(
          this.translate.instant('signUp.account-activated-text'),
          this.translate.instant('signUp.account-activated-title'),
        );
      });
  }

  isInLoginState(): boolean {
    const state = this.loginState === LoginStateEnum.login;
    if (state) {
      this.backButtonSupportService.show();
    } else {
      this.backButtonSupportService.hide();
    }
    return state;
  }

  login($event: { email: string; password: string }) {
    this.loginEmail = $event.email;

    const loginInfo = {
      email: $event.email,
      password: $event.password,
    } as UserLoginDto;

    this.waiting = true;
    this.loginService
      .login(loginInfo)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: async (successLoginDto) => {
          this.guiParams.load();
          await this.seonService.sendUserFingerprint(SeonValidationActionType.ACCOUNT_LOGIN);
          this.redirectAfterLoggedInTo(this.redirect ?? successLoginDto.homepage);
        },
        error: (error: any) => this.handleError(error),
      })
      .add(() => {
        this.waiting = false;
      });
  }

  twoFactorCodeEntered(code: string) {
    if (!this.originalRequest) {
      return;
    }

    this.waiting = true;

    this.twoFactor
      .redoRequestWith2FA(this.originalRequest, code, true)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: async (value) => {
          this.originalRequest = null;
          // if homepage is set on value, it's SuccessLoginDto -> login request
          if (value?.homepage) {
            this.guiParams.load();
            await this.seonService.sendUserFingerprint(SeonValidationActionType.ACCOUNT_LOGIN);
            this.redirectAfterLoggedInTo(this.redirect ?? value.homepage);
          }
        },
        error: (error) => this.handleError(error),
      })
      .add(() => {
        this.waiting = false;
      });
  }

  loginTrezor() {
    this.loginState = this.templateLoginStateEnum.twoFactorTrezor;
    this.trezor.performLogin$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: async (successLoginDto) => {
          this.guiParams.load();
          await this.seonService.sendUserFingerprint(SeonValidationActionType.ACCOUNT_LOGIN);
          this.redirectAfterLoggedInTo(this.redirect ?? successLoginDto.homepage);
        },
        error: () => {
          this.gtmService.pushTag({
            event: 'login_trezor_error',
            method: 'trezor',
          });
          this.loginState = this.templateLoginStateEnum.login;
          this.toastr.error(
            this.translate.instant('shared.trezor.error-message'),
            this.translate.instant('shared.trezor.error-title'),
          );
        },
      })
      .add(() => {
        this.waiting = false;
      });
  }

  private loginError(error: any, title?: string, message?: string) {
    this.toastr.error(
      this.translate.instant(message ?? 'login.login-error-message'),
      this.translate.instant(title ?? 'login.login-error-title'),
    );
  }

  private handleError(error: any) {
    if (LoginBaseComponent.isCsrfException(error)) {
      this.guiParams.load();
      this.guiParams.guiParams$
        .pipe(take(1))
        .subscribe(() => this.loginError(error, 'login.login-error-title', 'login.login-error-csrf-message'));

      return;
    }

    if (LoginBaseComponent.isTwoFactorAuthenticationException(error)) {
      const twoFactorType = error.error.additionalInfo.twoFactorType;
      this.originalRequest = this.twoFactor.popRequest(error.url) || null;

      const newLogInState = LoginBaseComponent.getLogInState(twoFactorType);
      if (newLogInState) {
        this.loginState = newLogInState;
        if (newLogInState === LoginStateEnum.twoFactorTrezor) {
          this.loginTrezor();
        }
        return;
      }
    }

    if (LoginBaseComponent.isInvalid2FA(error)) {
      this.twoFaLoginComponent.resetCode();
      this.loginError(error, 'login.2fa.2fa-error-title', 'login.2fa.2fa-error-message');
      return;
    }

    if (LoginBaseComponent.isWrongCredentialsError(error)) {
      this.loginError(error, 'login.login-error-title', 'login.login-error-message');
      return;
    }

    this.loginError(error, 'login.login-error-title', 'login.login-error-message-common');
  }
}
